import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes';
import { ConfirmationDialogComponent } from '@app/components';
import { BadgeOptions } from '@app/interfaces';
import { Employee } from '@app/models/employee/employee.model';
import { Project } from '@app/models/time-tracking/project.model';
import { ProjectEmployeeAssignmentService } from '@app/modules/time-tracking/services';
import { AuthService, NotifyService } from '@app/services';
import { Translatable } from '@app/types/translatable.type';
import { minutesToHoursAndMinutes } from '@time-tracking/functions/minutes-to-hours-and-minutes';

@Component({
    templateUrl: './time-tracking-show.template.html',
})
export class TimeTrackingShowView {
    @ViewChild('confirmDeleteModal') confirmDeleteModal!: ConfirmationDialogComponent;
    @ViewChild('confirmArchiveModal') confirmArchiveModal!: ConfirmationDialogComponent;
    @ViewChild('archiveInsteadModal') archiveInsteadModal!: ConfirmationDialogComponent;

    timeTrackingProject = new Project();
    employees: Employee[] = [];
    employeesOriginalAssignments: Employee[] = [];
    owners: Employee[] = [];
    minutesToHoursAndMinutes = minutesToHoursAndMinutes;

    approvalFlows: { [key: string]: Translatable } = {
        automatic: 'time-tracking.approvalFlows.automatic',
        manager: 'time-tracking.approvalFlows.manager',
        owner: 'time-tracking.approvalFlows.owner',
    };

    isLoading = false;
    isAdmin = false;

    /**
     * showContextMenu controls the context menu.
     * We don't always have options to show.
     */
    showContextMenu = false;

    constructor(
        private notifyService: NotifyService,
        private projectEmployeeAssignmentService: ProjectEmployeeAssignmentService,
        private route: ActivatedRoute,
        private router: Router,
        private authService: AuthService
    ) {
        this.timeTrackingProject = this.route.snapshot.data.models.get('project');
        this.employees = this.timeTrackingProject.projectAssignments.map(
            (projectAssignment) => projectAssignment.employee
        );
        this.createEmployeesOriginalAssignments();

        this.owners = this.timeTrackingProject.projectOwnerships.map((projectOwnership) => projectOwnership.owner);
        this.owners.sort((a, b) => (a.firstName > b.firstName ? 1 : -1));
        this.isAdmin = this.authService.can(Platform.timeTracking.permission.view);

        this.setShowContextMenu();
    }

    async onDelete(): Promise<void> {
        // we don't allow you to delete an archived project
        if (this.timeTrackingProject.hasTimesheetEntries) {
            const justClose = await this.archiveInsteadModal.open();
            if (!justClose) {
                this.archiveProject();
            }
            return;
        }

        const shouldDelete = await this.confirmDeleteModal.open();
        if (!shouldDelete) {
            return;
        }

        this.isLoading = true;

        try {
            await this.timeTrackingProject.delete();
            this.router.navigate(['..'], { relativeTo: this.route });
        } catch (e) {
            this.notifyService.error('time-tracking.deleteError');
            this.isLoading = false;
        }
    }

    onEdit(): void {
        this.router.navigate(['edit'], { relativeTo: this.route });
    }

    async archiveProject(): Promise<void> {
        const shouldArchive = await this.confirmArchiveModal.open();
        if (!shouldArchive) {
            return;
        }

        this.isLoading = true;

        try {
            this.timeTrackingProject.isArchived = true;
            await this.timeTrackingProject.save();
            this.setShowContextMenu();
        } catch (error) {
            this.notifyService.error('time-tracking.archiveError');
        }

        this.isLoading = false;
    }

    async markAsActive(): Promise<void> {
        this.isLoading = true;

        try {
            this.timeTrackingProject.isArchived = false;
            await this.timeTrackingProject.save();
            this.setShowContextMenu();
        } catch (error) {
            this.notifyService.error('time-tracking.markAsActiveError');
        }

        this.isLoading = false;
    }

    async onDeleteAssignment(employee: Employee): Promise<void> {
        try {
            await this.projectEmployeeAssignmentService.assign(
                [],
                [{ projectId: this.timeTrackingProject.id, employeeId: employee.id }]
            );
            this.notifyService.success('time-tracking.employeeRemoved');
        } catch (error) {
            this.notifyService.error('time-tracking.unassignFromTimeTrackingError');
        }

        this.reload();
    }

    async reload(): Promise<void> {
        try {
            this.timeTrackingProject = await Project.with(['assignments.employee']).find(this.timeTrackingProject.id);

            this.employees = this.timeTrackingProject.projectAssignments.map(
                (projectAssignment) => projectAssignment.employee
            );

            this.createEmployeesOriginalAssignments();
            this.setShowContextMenu();
        } catch (err) {
            this.notifyService.error(err);
        }
    }

    async projectEmployeeAssignments(employees: Employee[]): Promise<void> {
        const attachEmployees = employees.filter((employee) => {
            return !this.employeesOriginalAssignments.some((originalEmployee) => {
                return employee.id === originalEmployee.id;
            });
        });

        const detachEmployees = this.employeesOriginalAssignments.filter((originalEmployee) => {
            return !employees.some((employee) => {
                return employee.id === originalEmployee.id;
            });
        });

        if (attachEmployees.length + detachEmployees.length === 0) {
            return;
        }

        try {
            await this.projectEmployeeAssignmentService.assign(
                this.projectEmployeeAssignmentService.createForProject(this.timeTrackingProject, attachEmployees),
                this.projectEmployeeAssignmentService.createForProject(this.timeTrackingProject, detachEmployees)
            );
            this.notifyService.success('time-tracking.assignedEmployeesUpdated');
        } catch (error) {
            this.notifyService.error('time-tracking.assignToTimeTrackingError');
        }

        this.reload();
    }

    headerBadgeOptions(): BadgeOptions {
        if (this.timeTrackingProject.isArchived) {
            return {
                label: 'time-tracking.status.archived',
                type: 'danger',
            };
        }

        return {
            label: 'time-tracking.status.active',
            type: 'informative',
        };
    }

    isOwnerApprovalFlow(): boolean {
        return this.timeTrackingProject.approvalFlow === 'owner';
    }

    private createEmployeesOriginalAssignments(): void {
        // This will be used to tell the diff for detaching and attaching employees
        this.employeesOriginalAssignments = [
            ...this.timeTrackingProject.projectAssignments.map((projectAssignment) => projectAssignment.employee),
        ];
    }

    private setShowContextMenu(): void {
        const project = this.timeTrackingProject;

        if (!project.isArchived) {
            this.showContextMenu = true;
            return;
        }

        // if project is archived, only possible option is delete
        // but this is only shown to admins and only when there are no timesheet entries
        this.showContextMenu = this.isAdmin && !project.hasTimesheetEntries;
    }
}
